import { gql } from '@apollo/client'

/**
 * gets the string for `order` query
 * returns a single order, for the Order Detail screen
 * @internal
 */

/**
 * The `Order` selection the Order Detail screen and the in-webview order
 * confirmation both read.
 *
 * Shared rather than duplicated, the way `prefillCart` shares
 * `cartFieldsQuery`, and for a reason beyond DRY: the confirmation flow writes
 * the order it gets from `createOrder` / `confirmOrder` into the slice and
 * then replaces it with what a poll tick reads from this query. Two different
 * selections would mean the screen's content changed on the first tick — a
 * field the mutation did not ask for arriving from nowhere, or one it did
 * disappearing.
 *
 * Indented to sit under a field selection two levels deep, following
 * `cartFieldsQuery`. The interpolation is whitespace-only different from the
 * document that was inline here before, so `print(orderQuery)` — which the
 * Order Detail tests assert on — is unchanged.
 *
 * **`storeCode` (COM-889) needed no rollout gate, and that is a fact about
 * the field rather than about this selection.** Everything else here is
 * subject to the constraint the repo's `CLAUDE.md` states — a field this
 * string selects is requested by the `order` query, `createOrderMutation` and
 * `confirmOrderMutation` at once, and GraphQL validates a whole document
 * against the deployed schema before executing any of it, so a field the
 * running storefront does not declare breaks all three outright rather than
 * arriving `null`. `subtotal` (COM-739) is what that costs when skipped.
 * `storeCode` is exempt because it is not new: it sits on
 * `orderCommonTypeDefs` (`packages/graphql-schemas/src/types/commonFields.ts`
 * in storefront), which the user schema's `type Order` composes, and it is
 * present in every tag either environment could be running or roll back to —
 * checked against `v3.41.3`, `v3.41.5`, `v3.41.6` and `v3.42.0`, with
 * production on `v3.41.5` and staging on `v3.42.0` at the time of writing.
 * Do not read that as "adding a field here is fine"; read it as "this one was
 * checked, one tag at a time, and the next one has to be too".
 * @internal
 */
export const orderFieldsQuery: string = `
      id
      sequence
      status
      externalRefStatus
      createdAt
      subtotal
      total
      totalFee
      compensations
      discountsByPromotion
      customFields
      activeFees {
        total
        fee {
          fee
          threshold
          type
        }
      }
      paymentMethod
      expectedDeliveryDate
      storeCode
      customer {
        address
        city
        state
        zipCode
        country
        region
      }
      items {
        sku
        name
        description
        quantity
        price
        unitPrice
        imageURL
        packageType
        unitsPerPackage
        discounts
        customFields
      }
`

/*
 * `sessionUid` is sent alongside `id` on every call, and that is a security
 * property rather than a style choice. This is the authoritative statement of
 * it; every other site that touches the argument points back here.
 *
 * The user API declares both arguments as optional
 * (`order(storefrontName: String!, sessionUid: ID, id: ID)`) and branches on
 * them. With both, the resolver reads `customerUid` off the session and adds
 * it to the filter, so the shopper can only ever read their own orders. With
 * `id` alone it takes the legacy contract, where the session middleware loads
 * the order first and validates the session recorded *on the order* rather
 * than the caller's — a supported shape, but one that applies no scoping to
 * whoever is asking. Dropping `sessionUid` here moves the screen onto it.
 *
 * The value that reaches that branch is an omitted or `undefined` argument,
 * not an empty string. `validateOrder` puts both ids through
 * `joi.string().hex().length(24)` before the resolver branches, so `''` and
 * `null` are rejected outright as a 400 — loud, and harmless. An omitted
 * argument passes validation untouched, and Apollo drops an `undefined`
 * variable from the request body, so `undefined` and omitted are the same
 * request on the wire. `state.defaultSlice.sessionId` is initialised
 * `undefined`, which is what makes this reachable from an ordinary render
 * rather than theoretical. Hence the guard in `useGetOrderDetail`, which is
 * the only way into `buildVariables`.
 *
 * Scoping is by customer rather than by session on purpose, server-side: an id
 * created in an earlier session still resolves for the same shopper.
 */
const orderQuery = gql`
  query Order($storefrontName: String!, $sessionUid: ID, $id: ID) {
    order(storefrontName: $storefrontName, sessionUid: $sessionUid, id: $id) {
      ${orderFieldsQuery}
    }
  }
`

export default orderQuery
